'use client';

import { ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTenantSlug } from '@/hooks/use-tenant-slug';
import { REGISTRATION_THEME_CLASSES, type RegistrationTheme } from './theme';

interface StepFormActionsProps {
  stepNumber: number;
  totalSteps?: number;
  stepLabel: string;
  submitFormId: string;
  submitLabel: string;
  loadingLabel: string;
  isLoading: boolean;
  showBack: boolean;
  onBack?: () => void;
  backLabel: string;
  isKeyboardOpen?: boolean;
  submitHref?: string;
  onSubmitClick?: () => void;
  backVariant?: 'ghost' | 'outline';
  backClassName?: string;
  submitClassName?: string;
  /**
   * Visual theme. 'dark' (default) keeps the original stone/gold literals used
   * by the DGR/DGD register pages; 'semantic' uses shadcn tokens so the
   * [data-tenant] CSS scope controls the palette (registration engine).
   */
  theme?: RegistrationTheme;
}

export function StepFormActions({
  stepNumber,
  totalSteps = 4,
  stepLabel,
  submitFormId,
  submitLabel,
  loadingLabel,
  isLoading,
  showBack,
  onBack,
  backLabel,
  isKeyboardOpen = false,
  submitHref,
  onSubmitClick,
  backVariant = 'ghost',
  backClassName,
  submitClassName,
  theme = 'dark',
}: StepFormActionsProps) {
  const c = REGISTRATION_THEME_CLASSES[theme];
  const tenantSlug = useTenantSlug();
  const isDgd = tenantSlug === 'dgd';
  // Dark theme keeps the tenant-derived gold accents (DGR/DGD); semantic
  // defers to the [data-tenant] scope via primary tokens.
  const accentBg =
    theme === 'semantic'
      ? 'bg-primary hover:bg-primary/90'
      : isDgd
        ? 'bg-dgd-gold hover:bg-dgd-gold-light'
        : 'bg-dgr-gold hover:bg-dgr-gold-light';
  const progressBg = theme === 'semantic' ? 'bg-primary' : isDgd ? 'bg-dgd-gold' : 'bg-dgr-gold';
  const backButtonClassName = backVariant === 'outline' ? c.actionsBackOutline : c.actionsBackGhost;
  const primaryButtonClassName =
    submitClassName ?? `${accentBg} px-8 font-semibold ${c.actionsPrimaryText} disabled:opacity-50`;
  const mobilePrimaryButtonClassName =
    submitClassName ??
    `min-w-0 justify-center whitespace-normal ${accentBg} px-6 py-3 text-center ${c.actionsPrimaryText} disabled:cursor-not-allowed disabled:opacity-50`;

  return (
    <>
      <div className={c.actionsDesktopBar}>
        {showBack ? (
          <Button
            type="button"
            variant={backVariant}
            onClick={onBack}
            className={backClassName ?? backButtonClassName}
          >
            {backLabel}
          </Button>
        ) : (
          <div />
        )}
        {submitHref ? (
          <Button asChild className={primaryButtonClassName}>
            <a href={submitHref}>
              {submitLabel}
              <ChevronRight className="ml-1 h-4 w-4" />
            </a>
          </Button>
        ) : (
          <Button
            type={onSubmitClick ? 'button' : 'submit'}
            form={onSubmitClick ? undefined : submitFormId}
            onClick={onSubmitClick}
            disabled={isLoading}
            className={primaryButtonClassName}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {loadingLabel}
              </>
            ) : (
              <>
                {submitLabel}
                <ChevronRight className="ml-1 h-4 w-4" />
              </>
            )}
          </Button>
        )}
      </div>

      <div
        className={`${c.actionsMobileBar} ${isKeyboardOpen ? 'gap-2' : 'gap-2.5'}`}
        style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))' }}
      >
        {/* Progress bar */}
        <div className="flex items-center gap-3">
          <span className={c.actionsProgressStep}>
            {stepNumber}/{totalSteps}
          </span>
          <div className={c.actionsProgressTrack}>
            <div
              className={`h-full rounded-full ${progressBg} transition-all duration-300`}
              style={{ width: `${(stepNumber / totalSteps) * 100}%` }}
            />
          </div>
          <span className={c.actionsStepLabel}>{stepLabel}</span>
        </div>

        {/* Buttons — single row */}
        <div className="flex items-center gap-2.5">
          {showBack ? (
            <Button
              type="button"
              variant={backVariant}
              onClick={onBack}
              className={backClassName ?? `shrink-0 px-4 whitespace-normal ${backButtonClassName}`}
            >
              {backLabel}
            </Button>
          ) : null}

          {submitHref ? (
            <Button asChild className={`flex-1 ${mobilePrimaryButtonClassName}`}>
              <a href={submitHref}>
                {submitLabel}
                <ChevronRight className="ml-1 h-4 w-4" />
              </a>
            </Button>
          ) : (
            <Button
              type={onSubmitClick ? 'button' : 'submit'}
              form={onSubmitClick ? undefined : submitFormId}
              onClick={onSubmitClick}
              disabled={isLoading}
              className={`flex-1 ${mobilePrimaryButtonClassName}`}
            >
              {isLoading ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {loadingLabel}
                </span>
              ) : (
                <>
                  {submitLabel}
                  <ChevronRight className="ml-1 h-4 w-4" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </>
  );
}
